import { editor } from 'monaco-editor';
import { Monaco } from '@monaco-editor/react';

import { SchemaErrorMessages, EditorSchemaObject, TextTranslateFunction } from './types';
import { JSON_MARKERS_OWNER, MODEL_ERROR_PATTERNS, SCHEMA_I18N_KEYS } from './const';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return value != undefined && typeof value === 'object' && !Array.isArray(value);
};

const generateSchemaTitle = (
  original: unknown,
  schema: EditorSchemaObject,
  translate: TextTranslateFunction
): string | undefined => {
  if ('$ref' in schema) {
    const ref = schema.$ref as string;
    const parts = ref.split('/');
    const name = parts[parts.length - 1];
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  if ('type' in schema && 'minimum' in schema && schema.type === 'integer' && schema.minimum === 0) {
    return translate(SCHEMA_I18N_KEYS.TITLE_NON_NEGATIVE_INTEGER);
  }

  return undefined;
};

const generateSchemaDescription = (
  original: unknown,
  schema: EditorSchemaObject,
  translate: TextTranslateFunction
): string | undefined => {
  if ('type' in schema && 'minimum' in schema && schema.type === 'integer' && schema.minimum === 0) {
    return translate(SCHEMA_I18N_KEYS.DESC_NON_NEGATIVE_INTEGER);
  }

  if ('type' in schema && schema.type === 'array' && 'minItems' in schema) {
    return translate(SCHEMA_I18N_KEYS.DESC_ARRAY_WITH_ITEMS);
  }

  return undefined;
};

export const addValidationErrors = <T>(schema: T, translate: TextTranslateFunction): T => {
  if (Array.isArray(schema)) {
    return (schema.map((item) => addValidationErrors(item, translate)) as unknown) as T;
  }

  if (!isPlainObject(schema)) {
    return schema;
  }

  const clone: EditorSchemaObject = { ...schema };
  const errorMessage: SchemaErrorMessages = {};

  // Type validation
  if ('type' in clone && clone.type) {
    const type = Array.isArray(clone.type) ? clone.type.join(' or ') : clone.type;
    errorMessage.type = translate(SCHEMA_I18N_KEYS.TYPE, { type: String(type) });
  }

  // String validations
  if ('minLength' in clone && typeof clone.minLength === 'number') {
    errorMessage.minLength = translate(SCHEMA_I18N_KEYS.MIN_LENGTH, { count: clone.minLength });
  }
  if ('maxLength' in clone && typeof clone.maxLength === 'number') {
    errorMessage.maxLength = translate(SCHEMA_I18N_KEYS.MAX_LENGTH, { count: clone.maxLength });
  }
  if ('pattern' in clone && typeof clone.pattern === 'string') {
    errorMessage.pattern = translate(SCHEMA_I18N_KEYS.PATTERN);
  }

  // Number validations
  if ('minimum' in clone && typeof clone.minimum === 'number') {
    errorMessage.minimum = translate(SCHEMA_I18N_KEYS.MINIMUM, { value: clone.minimum });
  }
  if ('maximum' in clone && typeof clone.maximum === 'number') {
    errorMessage.maximum = translate(SCHEMA_I18N_KEYS.MAXIMUM, { value: clone.maximum });
  }
  if ('exclusiveMinimum' in clone && typeof clone.exclusiveMinimum === 'number') {
    errorMessage.exclusiveMinimum = translate(SCHEMA_I18N_KEYS.EXCLUSIVE_MINIMUM, { value: clone.exclusiveMinimum });
  }
  if ('exclusiveMaximum' in clone && typeof clone.exclusiveMaximum === 'number') {
    errorMessage.exclusiveMaximum = translate(SCHEMA_I18N_KEYS.EXCLUSIVE_MAXIMUM, { value: clone.exclusiveMaximum });
  }
  if ('multipleOf' in clone && typeof clone.multipleOf === 'number') {
    errorMessage.multipleOf = translate(SCHEMA_I18N_KEYS.MULTIPLE_OF, { value: clone.multipleOf });
  }

  // Array validations
  if ('minItems' in clone && typeof clone.minItems === 'number') {
    const key = clone.minItems === 1 ? SCHEMA_I18N_KEYS.MIN_ITEMS : SCHEMA_I18N_KEYS.MIN_ITEMS_PLURAL;
    errorMessage.minItems = translate(key, { count: clone.minItems });
  }
  if ('maxItems' in clone && typeof clone.maxItems === 'number') {
    const key = clone.maxItems === 1 ? SCHEMA_I18N_KEYS.MAX_ITEMS : SCHEMA_I18N_KEYS.MAX_ITEMS_PLURAL;
    errorMessage.maxItems = translate(key, { count: clone.maxItems });
  }
  if ('uniqueItems' in clone && clone.uniqueItems === true) {
    errorMessage.uniqueItems = translate(SCHEMA_I18N_KEYS.UNIQUE_ITEMS);
  }

  // Object validations
  if ('minProperties' in clone && typeof clone.minProperties === 'number') {
    const key = clone.minProperties === 1 ? SCHEMA_I18N_KEYS.MIN_PROPERTIES : SCHEMA_I18N_KEYS.MIN_PROPERTIES_PLURAL;
    errorMessage.minProperties = translate(key, { count: clone.minProperties });
  }
  if ('maxProperties' in clone && typeof clone.maxProperties === 'number') {
    const key = clone.maxProperties === 1 ? SCHEMA_I18N_KEYS.MAX_PROPERTIES : SCHEMA_I18N_KEYS.MAX_PROPERTIES_PLURAL;
    errorMessage.maxProperties = translate(key, { count: clone.maxProperties });
  }
  if ('required' in clone && Array.isArray(clone.required)) {
    const key = clone.required.length === 1 ? SCHEMA_I18N_KEYS.REQUIRED : SCHEMA_I18N_KEYS.REQUIRED_PLURAL;
    errorMessage.required = translate(key);
  }

  // Enum validation
  if ('enum' in clone && Array.isArray(clone.enum)) {
    errorMessage.enum = translate(SCHEMA_I18N_KEYS.ENUM);
  }

  // Const validation
  if ('const' in clone) {
    errorMessage.const = translate(SCHEMA_I18N_KEYS.CONST);
  }

  // Add errorMessage only if we have messages
  if (Object.keys(errorMessage).length) {
    clone.errorMessage = isPlainObject(schema.errorMessage)
      ? {
          ...errorMessage,
          ...(schema.errorMessage as SchemaErrorMessages),
        }
      : errorMessage;
  }

  // Add title and description for common patterns
  if (!clone.title) {
    clone.title = generateSchemaTitle(schema, clone, translate);
  }

  if (!clone.description) {
    clone.description = generateSchemaDescription(schema, clone, translate);
  }

  // Get required fields list for adding validation to nested properties
  const requiredFields =
    'required' in clone && Array.isArray(clone.required) ? new Set(clone.required as string[]) : new Set<string>();

  // Recursively process nested schemas
  for (const [key, value] of Object.entries(clone)) {
    if (key === 'errorMessage' || key === 'title' || key === 'description') {
      continue;
    }

    // Special handling for properties object to mark required fields
    if (requiredFields.size && key === 'properties' && isPlainObject(value)) {
      const processedProperties: Record<string, unknown> = {};

      for (const [propertyKey, propertyValue] of Object.entries(value)) {
        const processedProperty = addValidationErrors(propertyValue, translate);

        // Add required validation error to the property if it's in the required list
        if (requiredFields.has(propertyKey) && isPlainObject(processedProperty)) {
          const propertyWithRequired = { ...processedProperty };
          const propertyErrorMessage = { ...((propertyWithRequired.errorMessage as SchemaErrorMessages) || {}) };
          propertyErrorMessage.required = translate(SCHEMA_I18N_KEYS.REQUIRED);
          propertyWithRequired.errorMessage = propertyErrorMessage;
          processedProperties[propertyKey] = propertyWithRequired;
        } else {
          processedProperties[propertyKey] = processedProperty;
        }
      }

      clone[key] = processedProperties;
    } else {
      clone[key] = addValidationErrors(value, translate);
    }
  }

  return (clone as unknown) as T;
};

// NOTE: We have to flatten 'errorMessage' field because Monaco only supports base JSON schema
// validators without specialized ones from Ajv JSON Schema library.
export const flattenErrorMessages = <T>(schema: T, translate: TextTranslateFunction): T => {
  if (Array.isArray(schema)) {
    return (schema.map((item) => flattenErrorMessages(item, translate)) as unknown) as T;
  }

  if (!isPlainObject(schema)) {
    return schema;
  }

  const clone: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(schema)) {
    if (key === 'errorMessage' && isPlainObject(value)) {
      const liens = [
        translate('Schema: Errors summary'),
        ...Object.values(value)
          .filter((value): value is string => typeof value === 'string')
          .map((value) => `- ${value}`),
      ];
      clone[key] = liens.join('\n');
    } else {
      clone[key] = flattenErrorMessages(value, translate);
    }
  }

  return clone as T;
};

export const localizeModelMarkers = <T extends editor.IMarker>(markers: T[], translate: TextTranslateFunction): T[] => {
  return markers.map((marker) => {
    for (const rule of MODEL_ERROR_PATTERNS) {
      const match = marker.message.match(rule.regex);
      if (match) {
        const params = rule.params(match, marker.message);
        return {
          ...marker,
          message: translate(rule.key, params),
        };
      }
    }
    return marker;
  });
};

export const setupContentObservation = (
  editor: editor.IStandaloneCodeEditor,
  monaco: Monaco,
  onStart?: (markers: editor.IMarker[], value?: string) => void,
  onChange?: (markers: editor.IMarker[], value?: string) => void
): void => {
  if (!onStart && !onChange) {
    return;
  }

  const model = editor.getModel();
  if (!model) {
    return;
  }

  if (onStart) {
    const markers = monaco.editor.getModelMarkers({ owner: JSON_MARKERS_OWNER, resource: model.uri });
    const value = editor.getValue();
    onStart(markers, value);
  }

  if (!onChange) {
    return;
  }

  editor.onDidChangeModelContent(() => {
    const markers = monaco.editor.getModelMarkers({ owner: JSON_MARKERS_OWNER, resource: model.uri });
    const value = editor.getValue();
    onChange(markers, value);
  });
};

export const setupMarkersObservation = (
  editor: editor.IStandaloneCodeEditor,
  monaco: Monaco,
  transform?: (markers: editor.IMarker[]) => editor.IMarker[],
  callback?: (markers: editor.IMarker[]) => void
): void => {
  if (!transform && !callback) {
    return;
  }

  const model = editor.getModel();
  if (!model) {
    return;
  }

  monaco.editor.onDidChangeMarkers(() => {
    const oldMarkers = monaco.editor.getModelMarkers({ owner: JSON_MARKERS_OWNER, resource: model.uri });
    const newMarkets = transform ? transform(oldMarkers) : oldMarkers;

    if (transform && newMarkets.some((marker, index) => marker.message !== oldMarkers[index].message)) {
      monaco.editor.setModelMarkers(model, JSON_MARKERS_OWNER, newMarkets);
    }

    callback?.(newMarkets);
  });
};
